// offlineReference — 離線編輯工項用的本機參考資料＋離線處理器（2026-10-01）。
//
// 使用者要求「編輯、新增工項要在離線版可以正常操作」。工項編輯彈窗、更換工項（進階搜尋）、
// 挑選工項、單價分析細項代碼、代碼規則精靈，都要查伺服器上的參考資料；認領離線編輯時
// （BudgetEdit.vue）呼叫 downloadOfflineReference() 把這些資料存進這台裝置，離線時由 Common/api.js
// 的「離線處理器」改用本機資料回答，呼叫端完全不用改：
//
//   /Budget/SearchPccItems        ← 工項主檔（這個使用者線上查得到的範圍）＋同一套四種篩法
//   /Budget/GetPccItem            ← 工項主檔精確查詢＋編碼規則比對（EvaluateRule／BuildRuleResult）
//   /ProjectItemCode/GetRuleTable ← 規則表（全站共用）
//   /Budget/GetChapterCounts      ← 伺服器算好的章碼工項數
//   /Budget/GetItemHistory        ← 歷史價格離線先不提供（使用者裁示暫緩），回空清單
//   /Budget/GetSharedProjects、/Budget/GetSharedItems ← 其他機關分享的案件不下載（量不固定），
//                                    回空清單並提示需要連線
//
// ⚠⚠⚠ 查詢與規則比對的純函式在 Common/offlineReferenceCore.js（跟後端 C# 的同構配對、交叉測試都寫在那裡），
//   這支只負責本機資料的下載／讀取與處理器登記。
// ⚠ 資料範圍：工項主檔是伺服器依同一套機關範圍給的（不會多給線上查不到的列）；規則表與章碼
//   計數是全站共用資料，線上本來就人人查得到。

import { idbGet, idbSet } from './offlineDb.js'
import { registerOfflineHandler } from './api.js'
import { ui } from './ui.js'
import { searchPccItems, findExactItem, evaluateRule, rulesToMap } from './offlineReferenceCore.js'

const KEY_ITEMS = 'ref:itemBase'
const KEY_RULES = 'ref:rules'
const KEY_RULES_VERSION = 'ref:rulesVersion'
const KEY_COUNTS = 'ref:chapterCounts'

let itemsCache = null      // [{ itemCode, nameZh, unit, unitPrice, laborRate, equipRate, materialRate, miscRate, codeKind }]
let rulesCache = null      // Map `${ruleKind}:${outline}` → rule（GetRuleTable 形狀）
let countsCache = null     // { [outlineCode]: n }

/**
 * 下載（或更新）這台裝置的參考資料。規則表帶版本戳，版本沒變就不重新下載。
 * 成功回 true；失敗回 false（不擋認領，只是離線時工項查詢會提示需要連線）。
 */
export async function downloadOfflineReference() {
  let cachedVersion = null
  try { cachedVersion = await idbGet(KEY_RULES_VERSION) } catch { cachedVersion = null }
  let resp
  try {
    resp = await window.myAjax.post('/Budget/GetOfflineReferenceBundle', { rulesVersion: cachedVersion || null },
      { suppressLoading: true, offlineSilent: true })
  } catch { return false }
  const d = resp?.data
  if (d?.result !== 0 || !d.data) return false
  const b = d.data
  try {
    await idbSet(KEY_ITEMS, b.itemBase || [])
    await idbSet(KEY_COUNTS, b.chapterCounts || {})
    if (Array.isArray(b.rules)) {
      await idbSet(KEY_RULES, b.rules)
      await idbSet(KEY_RULES_VERSION, b.rulesVersion || '')
      rulesCache = null
    }
  } catch { return false }
  itemsCache = b.itemBase || []
  countsCache = b.chapterCounts || {}
  return true
}

async function loadItems() {
  if (itemsCache) return itemsCache
  try { itemsCache = (await idbGet(KEY_ITEMS)) || null } catch { itemsCache = null }
  return itemsCache
}

async function loadRules() {
  if (rulesCache) return rulesCache
  let list = null
  try { list = await idbGet(KEY_RULES) } catch { list = null }
  if (!Array.isArray(list)) return null
  rulesCache = rulesToMap(list)
  return rulesCache
}

/** 本機規則表（evaluateRule／buildCodeAccuracy 用的 Map）；這台裝置沒有下載過回 null。 */
export async function loadOfflineRulesMap() {
  return loadRules()
}

async function loadCounts() {
  if (countsCache) return countsCache
  try { countsCache = (await idbGet(KEY_COUNTS)) || null } catch { countsCache = null }
  return countsCache
}

// ── 離線處理器 ────────────────────────────────────────────────────────

registerOfflineHandler('/Budget/SearchPccItems', async (body) => {
  const items = await loadItems()
  if (!items) return undefined
  return { result: 0, data: searchPccItems(body, items) }
})

registerOfflineHandler('/Budget/GetPccItem', async (body) => {
  const items = await loadItems()
  const rules = await loadRules()
  if (!items || !rules) return undefined
  return { result: 0, data: { item: findExactItem(body?.itemCode, items), rule: evaluateRule(body?.itemCode, rules) } }
})

registerOfflineHandler('/ProjectItemCode/GetRuleTable', async (body) => {
  const rules = await loadRules()
  if (!rules) return undefined
  const code = String(body?.outlineCode ?? '').trim()
  const kind = String(body?.ruleKind ?? '').trim() || 'N'
  const rule = code ? rules.get(`${kind}:${code}`) : null
  return {
    result: 0,
    rule: rule || {
      chapter: code, chapterName: '', prefix: '', note: '', noRuleTable: true, sourceFile: null,
      sourceVersion: null, importTime: null, importUserName: null, steps: [],
    },
  }
})

registerOfflineHandler('/Budget/GetChapterCounts', async (body) => {
  const counts = await loadCounts()
  if (!counts) return undefined
  const div = String(body?.divisionCode ?? '').trim()
  if (!div) return { counts }
  return { counts: Object.fromEntries(Object.entries(counts).filter(([code]) => code.slice(0, 2) === div)) }
})

// 歷史價格：使用者裁示離線先暫緩，回空清單（不報錯、不跳提示）。
registerOfflineHandler('/Budget/GetItemHistory', async () => ({ result: 0, data: { own: [], shared: [] } }))

let sharedNoticeShown = false
const sharedOffline = async () => {
  if (!sharedNoticeShown) { sharedNoticeShown = true; ui.toast('其他機關分享的案件需要連線才能查詢', 'warn') }
  return { result: 0, data: [] }
}
registerOfflineHandler('/Budget/GetSharedProjects', sharedOffline)
registerOfflineHandler('/Budget/GetSharedItems', sharedOffline)
